import { WelcomeMessage } from '@/components/features/chat/components/welcome/WelcomeMessage';
import Questionnaire from '@/components/features/questionnaire/Questionnaire';
import { motion } from 'framer-motion';
import React, { useMemo, useState } from 'react';
import { PredefinedButtons } from '@/components/features/chat/components/ui';
import { logger } from '@/lib/utils/logger';
import { messageVariants } from '@/lib/helpers/animations/variants';
import { Input } from '@/components/common/inputs/text/input';
import { Button } from '@/components/common/inputs/button/button';
import { Link, MapPin, Search } from 'lucide-react';
import { useRouter } from 'next/navigation';

export const WelcomeContent = React.memo(
  ({
    isFormLoading,
    isQuestionnaireSubmitted,
    savedFormData,
    isDemo,
    isChatLoading,
    handlePromptSubmission,
    formAnswerId,
    t,
  }: {
    isFormLoading: boolean;
    isQuestionnaireSubmitted: boolean;
    savedFormData: any;
    isDemo: boolean;
    isChatLoading: boolean;
    handlePromptSubmission: (text: string, formAnswerId: string) => void;
    formAnswerId: string;
    t: any;
  }) => {
    const [location, setLocation] = useState('');
    const router = useRouter();

    // Determine what content to show
    const { showQuestionnaire, showPredefinedButtons } = useMemo(() => {
      const shouldShowQuestionnaire =
        !isFormLoading &&
        (!isQuestionnaireSubmitted || isDemo || !savedFormData);

      const shouldShowButtons =
        !isFormLoading && isQuestionnaireSubmitted && savedFormData;

      return {
        showQuestionnaire: shouldShowQuestionnaire,
        showPredefinedButtons: shouldShowButtons,
      };
    }, [isFormLoading, isQuestionnaireSubmitted, savedFormData, isDemo]);

    const handleFindExperts = () => {
      router.push(`/advisors?location=${encodeURIComponent(location)}`);
    };

    return (
      <motion.div
        variants={messageVariants}
        initial='initial'
        animate='animate'
        exit='exit'
        className='flex h-full min-h-full w-full flex-col overflow-y-auto'
      >
        <div className='flex h-full min-h-full w-full flex-col items-center px-4 py-2 pb-12 sm:px-6 md:px-8'>
          <div className='my-auto w-full pb-12'>
            <div className='mx-auto w-full max-w-2xl'>
              <WelcomeMessage />
            </div>

            {showQuestionnaire && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className='mx-auto flex w-full max-w-2xl items-center justify-center'
              >
                <Questionnaire />
              </motion.div>
            )}

            {showPredefinedButtons && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className='mx-auto w-full max-w-2xl'
              >
                <PredefinedButtons
                  t={t}
                  isLoading={isChatLoading}
                  handlePromptSubmission={handlePromptSubmission}
                  formAnswerId={formAnswerId}
                />
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className='mx-auto w-full max-w-2xl'
            >
              <div className='hidden rounded-xl bg-gradient-to-r from-purple-50 to-purple-50 p-4 shadow-lg dark:from-purple-900/20 dark:to-purple-900/20 sm:p-6 md:p-8'>
                <h3 className='mb-4 text-center text-base font-semibold text-gray-800 dark:text-gray-100 sm:text-lg md:text-xl'>
                  Find Legal Experts Near You
                </h3>
                <div className='relative mx-auto max-w-md'>
                  <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                    <MapPin className='h-4 w-4 text-gray-400 sm:h-5 sm:w-5' />
                  </div>
                  <Input
                    type='text'
                    value={location}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setLocation(e.target.value)
                    }
                    placeholder='Enter your location...'
                    className='flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 pl-10 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700/50 dark:bg-gray-800/50 dark:placeholder:text-gray-500 sm:h-10 sm:text-base'
                  />
                </div>
                <Button
                  onClick={handleFindExperts}
                  className='mt-4 flex w-full items-center justify-center space-x-2 rounded-lg bg-gradient-to-r from-purple-600 to-purple-600 py-2 text-white transition-all duration-200 hover:from-purple-700 hover:to-purple-700 dark:from-purple-500 dark:to-purple-700 dark:hover:from-purple-600 dark:hover:to-purple-800'
                >
                  <Search className='h-4 w-4 sm:h-5 sm:w-5' />
                  <span className='text-sm sm:text-base'>
                    Find Legal Experts
                  </span>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }
);

WelcomeContent.displayName = 'WelcomeContent';
